import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { ROUTES } from '../../utils/constants';
import { fetchTrainingCurriculum, resolveOnDemandTraining, startLesson, startOnDemandTraining } from '../../services/trainingService';
import TrainingPageView from './TrainingPageView';

export default function TrainingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [curriculum, setCurriculum] = useState(null);
  const [onDemandLoading, setOnDemandLoading] = useState(false);
  const [onDemandPlan, setOnDemandPlan] = useState(null);
  const [onDemandError, setOnDemandError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetchTrainingCurriculum();
      setCurriculum(resp?.data || resp);
    } catch (err) {
      setError(err?.message || 'Failed to load training curriculum');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openLesson = useCallback(async (procedureId) => {
    try {
      await startLesson(procedureId);
    } catch {
      // Progress save is best-effort before navigation
    }
    navigate(`${ROUTES.TRAINING}/lesson/${procedureId}`);
  }, [navigate]);

  const handleOnDemandQuery = useCallback(async (query) => {
    setOnDemandLoading(true);
    setOnDemandError('');
    setOnDemandPlan(null);
    try {
      const resp = await resolveOnDemandTraining(query);
      const data = resp?.data || resp;
      if (!data?.found) {
        setOnDemandError('No matching training path found. Try rephrasing your request.');
        return;
      }
      setOnDemandPlan(data);
    } catch (err) {
      setOnDemandError(err?.message || 'Failed to resolve on-demand training');
    } finally {
      setOnDemandLoading(false);
    }
  }, []);

  const handleStartOnDemand = useCallback(async (plan) => {
    if (plan?.canStart === false) return;
    const procedureIds = plan?.procedureIdsForQueue
      || plan?.actionableProcedureIds
      || plan?.procedureIds
      || [];
    if (!procedureIds.length) return;
    try {
      await startOnDemandTraining({
        scenarioId: plan.scenarioId,
        title: plan.title,
        procedureIds,
      });
      await startLesson(procedureIds[0]);
    } catch {
      // best effort
    }
    navigate(`${ROUTES.TRAINING}/lesson/${procedureIds[0]}`);
  }, [navigate]);

  const handleStartRecommended = useCallback(async (procedureId) => {
    await openLesson(procedureId);
  }, [openLesson]);

  return (
    <MainLayout scrollMain>
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        <TrainingPageView
          loading={loading}
          error={error}
          curriculum={curriculum}
          onRefresh={load}
          onOpenLesson={openLesson}
          onStartRecommended={handleStartRecommended}
          onDemandQuery={handleOnDemandQuery}
          onDemandLoading={onDemandLoading}
          onDemandPlan={onDemandPlan}
          onDemandError={onDemandError}
          onStartOnDemand={handleStartOnDemand}
        />
      </div>
    </MainLayout>
  );
}
